import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useToast } from '@/features/ui/toast/useToast';
import { IconX } from './icons';

export type ProspectFormValues = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  location: string;
  stage: string;
  product_interest: string;
  value: number;
  notes: string;
};

const empty: ProspectFormValues = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company: '',
  location: '',
  stage: 'nouveau',
  product_interest: '',
  value: 0,
  notes: '',
};

type Props = {
  onClose: () => void;
  onSave: (values: ProspectFormValues) => Promise<string | null>;
};

export function ProspectForm({ onClose, onSave }: Props) {
  const toast = useToast();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const set =
    (key: keyof ProspectFormValues) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm({
        ...form,
        [key]: key === 'value' ? Number(e.target.value) || 0 : e.target.value,
      });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    const err = await onSave({ ...form, value: Number(form.value) || 0 });
    setSaving(false);
    if (err) toast.error(err);
    else onClose();
  }

  return (
    <div className="crm-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="crm-modal crm-modal--sheet" role="dialog" aria-labelledby="crm-prospect-form-title" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal-sticky">
          <h2 id="crm-prospect-form-title">Nouveau prospect</h2>
          <button type="button" className="crm-modal-x" onClick={onClose} aria-label="Fermer">
            <IconX />
          </button>
        </div>
        <form onSubmit={(e) => void submit(e)} className="crm-modal-body">
          <div className="grid-2">
            <div className="field">
              <label>Prénom *</label>
              <input required value={form.first_name} onChange={set('first_name')} />
            </div>
            <div className="field">
              <label>Nom *</label>
              <input required value={form.last_name} onChange={set('last_name')} />
            </div>
            <div className="field">
              <label>Email</label>
              <input type="email" value={form.email} onChange={set('email')} />
            </div>
            <div className="field">
              <label>Téléphone</label>
              <input value={form.phone} onChange={set('phone')} />
            </div>
          </div>
          <div className="field">
            <label>Entreprise</label>
            <input value={form.company} onChange={set('company')} />
          </div>
          <div className="field">
            <label>Lieu de résidence</label>
            <input value={form.location} onChange={set('location')} placeholder="Ville, Pays" />
          </div>
          <div className="field">
            <label>Étape</label>
            <select value={form.stage} onChange={set('stage')}>
              <option value="nouveau">Nouveau</option>
              <option value="contacte">Contacté</option>
              <option value="negociation">Négociation</option>
              <option value="gagne">Gagné</option>
              <option value="perdu">Perdu</option>
            </select>
          </div>
          <div className="grid-2">
            <div className="field">
              <label>Produit/Service d&apos;intérêt</label>
              <input value={form.product_interest} onChange={set('product_interest')} />
            </div>
            <div className="field">
              <label>Valeur estimée (€)</label>
              <input type="number" min={0} value={form.value} onChange={set('value')} />
            </div>
          </div>
          <div className="field">
            <label>Notes</label>
            <textarea rows={3} value={form.notes} onChange={set('notes')} />
          </div>
          <div className="crm-modal-actions crm-modal-actions--split">
            <button type="button" className="btn crm-btn-outline" onClick={onClose}>
              Annuler
            </button>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
